import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchHeroBooks } from '../../services/catalog.js';

/**
 * Dynamic words cycling smoothly in the main headline
 */
const DYNAMIC_WORDS = [
  'classic',
  'thriller',
  'sci-fi',
  'biography',
  'romance',
  'masterpiece',
  'island epic',
];

/**
 * High-speed local WebP fallback collection:
 * Guaranteed instant load (10-45KB per cover) and zero layout shift.
 */
const FALLBACK_BOOKS = [
  {
    id: 'maali-almeida',
    slug: 'the-seven-moons-of-maali-almeida',
    title: 'The Seven Moons of Maali Almeida',
    author: 'Shehan Karunatilaka',
    genre: 'Thriller / Mystery',
    tag: 'Booker Prize 2022',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/maali-almeida.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1544947950-fa07a98d237f?auto=format&fit=crop&w=700&q=80',
    blurb:
      'A searing afterlife noir set amidst the chaotic underworld of 1990 Colombo. War photographer Maali Almeida has seven moons to solve his own death.',
    year: '2022',
    accent: '#f59e0b',
  },
  {
    id: 'orbital',
    slug: 'orbital',
    title: 'Orbital',
    author: 'Samantha Harvey',
    genre: 'Sci-Fi / Space',
    tag: 'Booker Prize 2024',
    origin: 'International',
    coverUrl: '/covers/hero/orbital.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?auto=format&fit=crop&w=700&q=80',
    blurb:
      'Six astronauts aboard the International Space Station witness sixteen sunrises in twenty-four hours in luminous, philosophical prose.',
    year: '2024',
    accent: '#818cf8',
  },
  {
    id: 'funny-boy',
    slug: 'funny-boy',
    title: 'Funny Boy',
    author: 'Shyam Selvadurai',
    genre: 'Coming-of-Age / Fiction',
    tag: 'Lambda Literary Award',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/funny-boy.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1476275466078-4007374efbbe?auto=format&fit=crop&w=700&q=80',
    blurb:
      'An emotionally resonant coming-of-age chronicle set in Colombo during the turbulent political and cultural tensions of 1970s and 1980s Sri Lanka.',
    year: '1994',
    accent: '#06b6d4',
  },
  {
    id: 'the-great-gatsby',
    slug: 'the-great-gatsby',
    title: 'The Great Gatsby',
    author: 'F. Scott Fitzgerald',
    genre: 'Classic',
    tag: 'American Classic',
    origin: 'International',
    coverUrl: '/covers/hero/the-great-gatsby.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?auto=format&fit=crop&w=700&q=80',
    blurb:
      'The story of the fabulously wealthy Jay Gatsby and his new love for the beautiful Daisy Buchanan across Jazz Age Long Island.',
    year: '1925',
    accent: '#eab308',
  },
  {
    id: 'chinaman',
    slug: 'chinaman-the-legend-of-pradeep-mathew',
    title: 'Chinaman: The Legend of Pradeep Mathew',
    author: 'Shehan Karunatilaka',
    genre: 'Fiction / Mystery',
    tag: 'Commonwealth Book Prize',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/chinaman.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1532012164546-f432f2e3777f?auto=format&fit=crop&w=700&q=80',
    blurb:
      'An alcoholic sports journalist attempts to track down a mythical Sri Lankan spin bowler who vanished in the 1980s. Widely praised as one of the best novels about cricket ever written.',
    year: '2010',
    accent: '#10b981',
  },
  {
    id: 'intermezzo',
    slug: 'intermezzo',
    title: 'Intermezzo',
    author: 'Sally Rooney',
    genre: 'Romance / Contemporary',
    tag: '#1 NYT Bestseller',
    origin: 'International',
    coverUrl: '/covers/hero/intermezzo.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1512820790803-83ca734da794?auto=format&fit=crop&w=700&q=80',
    blurb:
      'An exquisite, polyphonic novel examining grief, desire, chess, and the delicate architecture of love between two brothers.',
    year: '2024',
    accent: '#e4e4e7',
  },
  {
    id: 'running-in-the-family',
    slug: 'running-in-the-family',
    title: 'Running in the Family',
    author: 'Michael Ondaatje',
    genre: 'Memoir / Biography',
    tag: 'Sri Lankan Memoir Classic',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/running-in-the-family.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=700&q=80',
    blurb:
      'A poetic, lyrical memoir of Michael Ondaatje returning to Sri Lanka in the late 1970s to uncover his eccentric and flamboyant family heritage.',
    year: '1982',
    accent: '#f97316',
  },
  {
    id: 'silent-patient',
    slug: 'the-silent-patient',
    title: 'The Silent Patient',
    author: 'Alex Michaelides',
    genre: 'Thriller',
    tag: '#1 Global Thriller',
    origin: 'International',
    coverUrl: '/covers/hero/silent-patient.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&w=700&q=80',
    blurb:
      'Alicia Berenson’s life is seemingly perfect. One evening she shoots her husband five times and never speaks another word.',
    year: '2019',
    accent: '#ef4444',
  },
  {
    id: 'anils-ghost',
    slug: 'anils-ghost',
    title: "Anil's Ghost",
    author: 'Michael Ondaatje',
    genre: 'Literary Noir',
    tag: 'Gov. General’s Award',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/anils-ghost.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=700&q=80',
    blurb:
      'Anil Tissera, a young forensic anthropologist born in Sri Lanka and educated abroad, returns to investigate a series of politically motivated murders.',
    year: '2000',
    accent: '#6366f1',
  },
  {
    id: 'klara-and-the-sun',
    slug: 'klara-and-the-sun',
    title: 'Klara and the Sun',
    author: 'Kazuo Ishiguro',
    genre: 'Sci-Fi / Modern',
    tag: 'Nobel Laureate Masterpiece',
    origin: 'International',
    coverUrl: '/covers/hero/klara-and-the-sun.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1516979187457-637abb4f9353?auto=format&fit=crop&w=700&q=80',
    blurb:
      'A luminous, deeply touching story of Klara, an Artificial Friend with outstanding observational qualities, exploring the mysteries of the human heart.',
    year: '2021',
    accent: '#14b8a6',
  },
  {
    id: 'reef',
    slug: 'reef',
    title: 'Reef',
    author: 'Romesh Gunesekera',
    genre: 'Island Literature',
    tag: 'Booker Shortlist',
    origin: 'Sri Lanka',
    coverUrl: '/covers/hero/reef.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=700&q=80',
    blurb:
      'The exquisite story of young Triton, who goes to work as a houseboy for a marine biologist in 1960s Sri Lanka, told through culinary passion and decaying reefs.',
    year: '1994',
    accent: '#0284c7',
  },
  {
    id: '1984',
    slug: '1984',
    title: '1984',
    author: 'George Orwell',
    genre: 'Classic / Dystopian',
    tag: 'Timeless Masterpiece',
    origin: 'International',
    coverUrl: '/covers/hero/1984.webp',
    unsplashBackup:
      'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=700&q=80',
    blurb:
      'A startling and haunting vision of totalitarian surveillance and truth manipulation that remains shockingly relevant across all generations.',
    year: '1949',
    accent: '#a855f7',
  },
];

export default function MeliusBookHero() {
  const navigate = useNavigate();
  const [wordIndex, setWordIndex] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);

  // Fetch curated hero books from the backend API with caching and load resilience
  const { data: serverBooks, isLoading } = useQuery({
    queryKey: ['hero-books'],
    queryFn: fetchHeroBooks,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const books = serverBooks && serverBooks.length > 0 ? serverBooks : FALLBACK_BOOKS;

  // 3D Carousel state & DOM refs for zero-overhead, 60-120 FPS GPU animation
  const stageRef = useRef(null);
  const sectionRef = useRef(null);
  const cardElementsRef = useRef([]);
  const scrollOffsetRef = useRef(0);
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const dragDirectionRef = useRef(null); // 'horizontal' | 'vertical' | null
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const lastXRef = useRef(0);
  const velocityRef = useRef(0);
  const isHoveredRef = useRef(false);
  const isVisibleRef = useRef(true);
  const isScrollingRef = useRef(false);
  const [viewportWidth, setViewportWidth] = useState(1200);

  // Pause animation loop when the hero section scrolls out of the viewport
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => { isVisibleRef.current = entry.isIntersecting; },
      { rootMargin: '100px 0px' }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // Pause carousel animation while the user is actively scrolling the page
  // so the browser compositor has 100% GPU frame budget for butter-smooth vertical scroll
  useEffect(() => {
    let scrollTimeout;
    const onScroll = () => {
      isScrollingRef.current = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isScrollingRef.current = false;
      }, 120);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  // Cycle headline dynamic word smoothly
  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlipping(true);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % DYNAMIC_WORDS.length);
        setIsFlipping(false);
      }, 300);
    }, 2800);

    return () => clearInterval(interval);
  }, []);

  // Update viewport width on window resize
  useEffect(() => {
    const handleResize = () => {
      if (stageRef.current) {
        setViewportWidth(stageRef.current.clientWidth || window.innerWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive card metrics: ~2:3 vertical book aspect ratio
  const isMobile = viewportWidth < 640;
  const isTablet = viewportWidth >= 640 && viewportWidth < 1024;
  const cardWidth = isMobile ? 180 : isTablet ? 230 : 275;
  const halfCardWidth = cardWidth * 0.5;
  const halfCardHeight = cardWidth * 0.75;
  const cardSpacing = cardWidth * (isMobile ? 0.74 : 0.70);
  const totalCards = books.length;
  const totalTrackWidth = totalCards * cardSpacing;

  // Direct 60/120FPS GPU Animation Loop (Bypasses React Re-renders Completely)
  useEffect(() => {
    let animId;
    let lastTime = performance.now();
    const halfTrack = totalTrackWidth * 0.5;
    const normDivisor = cardWidth * 1.35;
    const fovLimit = isMobile ? 1.75 : 2.25;

    const updateTransforms = (now) => {
      // Skip work when section is off-screen or during active page scrolling to free GPU
      if (!isVisibleRef.current || isScrollingRef.current) {
        lastTime = now;
        animId = requestAnimationFrame(updateTransforms);
        return;
      }

      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;

      if (!isDraggingRef.current) {
        // Friction damping
        if (Math.abs(velocityRef.current) > 0.05) {
          scrollOffsetRef.current += velocityRef.current;
          velocityRef.current *= 0.93;
        } else {
          // Base continuous autoscroll speed
          const baseSpeed = isHoveredRef.current ? 16 : 46;
          scrollOffsetRef.current += baseSpeed * dt;
        }

        // Seamless infinite wrap around
        if (scrollOffsetRef.current >= totalTrackWidth) {
          scrollOffsetRef.current -= totalTrackWidth;
        } else if (scrollOffsetRef.current < 0) {
          scrollOffsetRef.current += totalTrackWidth;
        }
      }

      // Update each DOM card node directly without triggering React re-renders
      const cards = cardElementsRef.current;
      const count = books.length;

      for (let i = 0; i < count; i++) {
        const el = cards[i];
        if (!el) continue;

        const cardRawPos = (i * cardSpacing - scrollOffsetRef.current) % totalTrackWidth;
        let relativeX = cardRawPos;
        if (relativeX < -halfTrack) relativeX += totalTrackWidth;
        else if (relativeX > halfTrack) relativeX -= totalTrackWidth;

        const normX = relativeX / normDivisor;
        const absNorm = Math.abs(normX);

        // Frustum culling: hide offscreen cards using visibility (avoids DOM reflow / layout thrashing)
        if (absNorm > fovLimit) {
          if (el.style.visibility !== 'hidden') {
            el.style.visibility = 'hidden';
          }
          continue;
        }

        if (el.style.visibility === 'hidden') {
          el.style.visibility = 'visible';
        }

        const rotateY = absNorm > 0.15
          ? (normX < 0 ? 25 + Math.min(absNorm * 9, 20) : -(25 + Math.min(absNorm * 9, 20)))
          : -normX * 160;

        const translateZ = Math.max(45 - absNorm * 50, -110);
        const scale = Math.max(1.10 - absNorm * 0.15, 0.72);
        const opacity = Math.max(1 - (absNorm - 0.35) * 0.35, 0.35);
        const zIndex = Math.round(50 - absNorm * 10);
        const translateY = -normX * normX * 6;

        const finalX = relativeX - halfCardWidth;
        const finalY = translateY - halfCardHeight;

        el.style.transform = `translate3d(${finalX.toFixed(1)}px, ${finalY.toFixed(1)}px, ${translateZ.toFixed(1)}px) rotateY(${rotateY.toFixed(1)}deg) scale(${scale.toFixed(3)})`;
        el.style.opacity = opacity.toFixed(2);
        el.style.zIndex = zIndex;
      }

      animId = requestAnimationFrame(updateTransforms);
    };

    animId = requestAnimationFrame(updateTransforms);
    return () => cancelAnimationFrame(animId);
  }, [totalTrackWidth, cardSpacing, cardWidth, halfCardWidth, halfCardHeight, books.length, isMobile]);

  // Pointer Drag & Touch Handling with gesture locking: distinguishes horizontal swipe vs vertical page scroll
  const handlePointerDown = (e) => {
    isPointerDownRef.current = true;
    isDraggingRef.current = false;
    dragDirectionRef.current = null;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    startXRef.current = clientX;
    startYRef.current = clientY;
    lastXRef.current = clientX;
    velocityRef.current = 0;
  };

  const handlePointerMove = useCallback((e) => {
    if (!isPointerDownRef.current) return;

    // If gesture already determined to be a vertical page scroll, do not interfere at all!
    if (dragDirectionRef.current === 'vertical') return;

    const currentX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const currentY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

    // Detect intent once finger moves beyond jitter threshold (8px)
    if (!dragDirectionRef.current) {
      const dx = Math.abs(currentX - startXRef.current);
      const dy = Math.abs(currentY - startYRef.current);

      if (dx < 8 && dy < 8) return;

      if (dy >= dx) {
        // Vertical swipe: user is scrolling the page!
        dragDirectionRef.current = 'vertical';
        isDraggingRef.current = false;
        return;
      } else {
        // Horizontal swipe: user wants to drag the carousel!
        dragDirectionRef.current = 'horizontal';
        isDraggingRef.current = true;
        lastXRef.current = currentX;
      }
    }

    if (dragDirectionRef.current === 'horizontal') {
      const delta = currentX - lastXRef.current;
      scrollOffsetRef.current -= delta;
      velocityRef.current = -delta * 0.8;
      lastXRef.current = currentX;
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    isPointerDownRef.current = false;
    isDraggingRef.current = false;
    dragDirectionRef.current = null;
  }, []);

  useEffect(() => {
    const onUp = () => handlePointerUp();
    window.addEventListener('pointerup', onUp, { passive: true });
    window.addEventListener('touchend', onUp, { passive: true });
    return () => {
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, [handlePointerUp]);

  // Handle Search Submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/new?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/new');
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-paper text-ink border-b border-rule select-none"
      aria-label="3D Cylindrical Book Carousel Hero"
    >
      {/* --- Minimalist Dot-Matrix Pattern & Ambient Lighting --- */}
      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-35"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(28, 25, 23, 0.16) 1.25px, transparent 1.25px)',
          backgroundSize: '20px 20px',
        }}
      />
      <div className="pointer-events-none absolute inset-0 z-0 bg-radial from-transparent via-paper/20 to-paper/95" />

      {/* --- 1. Top Section: Centered Main Headline & Sub-copy --- */}
      <div className="relative z-10 pt-5 sm:pt-8 md:pt-10 pb-1 text-center page-gutter max-w-3xl mx-auto">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-[2.65rem] tracking-tight text-ink max-w-[28ch] mx-auto text-balance leading-tight">
          Read any{' '}
          <span className="inline-block relative min-w-[130px] sm:min-w-[190px] text-center sm:text-left">
            <span
              className={`inline-block text-stamp italic-accent decoration-stamp transition-all duration-300 transform ${
                isFlipping
                  ? '-translate-y-2 opacity-0 scale-95 blur-xs'
                  : 'translate-y-0 opacity-100 scale-100 blur-none'
              }`}
            >
              {DYNAMIC_WORDS[wordIndex]}
            </span>
          </span>{' '}
          you imagine
        </h1>

        <p className="font-sans text-xs sm:text-sm md:text-base text-ink-60 mt-1.5 sm:mt-2 max-w-[48ch] mx-auto leading-relaxed text-balance">
          A curated library of timeless stories and modern masterpieces.
        </p>
      </div>

      {/* --- 2. 3D Cylindrical Cover-Flow Book Carousel (GPU Optimized) --- */}
      <div
        ref={stageRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onMouseEnter={() => {
          isHoveredRef.current = true;
        }}
        onMouseLeave={() => {
          isHoveredRef.current = false;
        }}
        className="relative z-20 w-full my-3 sm:my-5 h-[420px] sm:h-[490px] md:h-[550px] flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing"
        style={{
          perspective: '1200px',
          perspectiveOrigin: '50% 50%',
          touchAction: 'pan-y pinch-zoom',
          maskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)',
        }}
      >
        {/* Ambient Floor Shadow under Center */}
        <div className="pointer-events-none absolute inset-x-0 bottom-4 h-16 bg-black/10 blur-3xl rounded-full" />

        {/* Loading State: Zero-layout-shift 3D Shimmer Skeleton */}
        {isLoading && (!serverBooks || serverBooks.length === 0) ? (
          <div
            className="relative w-full h-full flex items-center justify-center animate-pulse"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {[-2, -1, 0, 1, 2].map((slot) => {
              const rotateY = slot === 0 ? 0 : slot < 0 ? 30 : -30;
              const scale = slot === 0 ? 1.08 : 0.82;
              const translateZ = slot === 0 ? 40 : -70;
              const opacity = slot === 0 ? 0.9 : 0.45;
              const xOffset = slot * (cardWidth * 0.72);

              return (
                <div
                  key={slot}
                  className="absolute top-1/2 left-1/2"
                  style={{
                    width: `${cardWidth}px`,
                    aspectRatio: '2/3',
                    transform: `translate3d(calc(-50% + ${xOffset}px), -50%, ${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
                    opacity,
                    transformStyle: 'preserve-3d',
                  }}
                >
                  <div className="w-full h-full rounded-xl sm:rounded-2xl bg-paper-deep/80 border border-rule-strong/40 shadow-xl overflow-hidden flex flex-col justify-end p-4">
                    <div className="w-16 h-2 bg-rule-strong/50 rounded mb-2" />
                    <div className="w-28 h-3.5 bg-ink-20 rounded mb-1.5" />
                    <div className="w-20 h-2 bg-rule-strong/40 rounded" />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* 3D Active Carousel Track with Hardware Compositing */
          <div
            className="relative w-full h-full flex items-center justify-center pointer-events-auto"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {books.map((book, index) => {
              // Initial layout values before RAF updates them smoothly
              const isInitialCenter = index === 0;
              const isInitialAdjacent = index === 1 || index === books.length - 1;

              return (
                <div
                  key={book.id || book.slug || index}
                  ref={(el) => {
                    cardElementsRef.current[index] = el;
                  }}
                  onClick={(e) => {
                    if (Math.abs(velocityRef.current) < 1.5) {
                      e.stopPropagation();
                      setSelectedBook(book);
                    }
                  }}
                  className="absolute top-1/2 left-1/2 will-change-transform select-none"
                  style={{
                    width: `${cardWidth}px`,
                    aspectRatio: '2/3',
                    transform: `translate3d(-${halfCardWidth}px, -${halfCardHeight}px, 0px) scale(0.8)`,
                    transformStyle: 'preserve-3d',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                  }}
                >
                  {/* Book Card with Realistic Spine, Cover, and Drop Shadow */}
                  <div
                    className="group relative w-full h-full rounded-xl sm:rounded-2xl overflow-hidden border border-black/20 bg-paper-raised shadow-[0_16px_32px_-8px_rgba(0,0,0,0.38)]"
                  >
                    {/* Modern WebP Book Cover with Optimized Resource Prioritization */}
                    <img
                      src={book.coverUrl}
                      alt={book.title}
                      loading={isInitialCenter || isInitialAdjacent ? 'eager' : 'lazy'}
                      fetchPriority={isInitialCenter ? 'high' : isInitialAdjacent ? undefined : 'low'}
                      decoding="async"
                      width="500"
                      height="750"
                      className="w-full h-full object-cover pointer-events-none"
                      onError={(e) => {
                        // Fallback to backup image or placeholder if local webp fails
                        if (e.target.src !== book.unsplashBackup && book.unsplashBackup) {
                          e.target.src = book.unsplashBackup;
                        }
                      }}
                    />

                    {/* Fallback Typographic Face underneath image */}
                    <div
                      className="absolute inset-0 p-4 flex flex-col justify-between text-white -z-10"
                      style={{ backgroundColor: book.accent || '#1c1917' }}
                    >
                      <span className="font-mono text-[9px] uppercase tracking-wider bg-white/20 px-1.5 py-0.5 rounded w-max">
                        {book.genre}
                      </span>
                      <div>
                        <h3 className="font-display text-sm font-bold leading-tight">{book.title}</h3>
                        <p className="font-sans text-xs text-white/80 mt-1">{book.author}</p>
                      </div>
                      <span className="font-mono text-[9px] text-amber-300 font-semibold truncate">
                        {book.tag}
                      </span>
                    </div>

                    {/* Realistic Book Spine Shadow & Edge Treatment (Single flattened overlay) */}
                    <div
                      className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/15"
                      style={{ boxShadow: 'inset 2px 0 3px rgba(255,255,255,0.12), inset -1px 0 2px rgba(0,0,0,0.35)' }}
                    />

                    {/* Hover Inspect Pill */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <span className="bg-stamp text-paper font-mono text-[10px] px-2 py-0.5 rounded-full font-bold shadow-md">
                        Inspect
                      </span>
                    </div>

                    {/* Bottom Shelf Label Stripe */}
                    <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/90 via-black/60 to-transparent text-white">
                      <p className="font-mono text-[9px] text-amber-300 uppercase tracking-wider truncate font-semibold">
                        {book.tag}
                      </p>
                      <p className="font-display text-xs sm:text-sm font-bold truncate leading-tight mt-0.5">
                        {book.title}
                      </p>
                      <div className="mt-1 text-[10px] text-white/80 font-mono truncate">
                        <span>{book.author}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* --- 3. Floating, Rounded Interactive Input Bar at Bottom --- */}
      <div className="relative z-10 pb-8 sm:pb-12 text-center page-gutter max-w-xl mx-auto">
        <form
          onSubmit={handleSearchSubmit}
          className="inline-flex items-center gap-3 px-4 py-2.5 sm:px-5 sm:py-3 rounded-full bg-white text-ink shadow-md hover:shadow-lg border border-rule-strong/60 hover:border-ink focus-within:border-ink focus-within:ring-2 focus-within:ring-stamp/20 transition-all w-full"
        >
          {/* Compass / Search Icon */}
          <span className="text-stamp flex items-center justify-center shrink-0" aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </span>

          {/* Interactive Input with Requested Placeholder */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search a title, author, or genre..."
            className="bg-transparent text-xs sm:text-sm text-ink placeholder:text-ink-40 outline-none flex-1 font-sans font-medium"
            aria-label="Search a title, author, or genre"
          />

          {/* Action Button */}
          <button
            type="submit"
            className="w-8 h-8 rounded-full bg-ink text-paper hover:bg-stamp flex items-center justify-center text-sm transition-colors shrink-0 cursor-pointer shadow-xs"
            title="Search catalog"
            aria-label="Submit search"
          >
            &rarr;
          </button>
        </form>

        <p className="font-mono text-[11px] text-ink-60 uppercase tracking-widest mt-3.5">
          &larr; Drag horizontally or click any book to inspect &rarr;
        </p>
      </div>

      {/* --- Click-to-Inspect Modal Drawer --- */}
      {selectedBook && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={selectedBook.title}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
          onClick={() => setSelectedBook(null)}
        >
          <div
            className="relative w-full max-w-lg bg-paper-raised border border-rule-strong rounded-2xl p-6 sm:p-8 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedBook(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full border border-rule hover:border-ink hover:bg-paper flex items-center justify-center font-mono text-sm text-ink cursor-pointer"
              aria-label="Close dialog"
            >
              &times;
            </button>

            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {/* Cover in Modal */}
              <div className="w-32 sm:w-40 aspect-[2/3] rounded-lg overflow-hidden border border-black/20 shadow-md shrink-0 bg-paper">
                <img
                  src={selectedBook.coverUrl}
                  alt={selectedBook.title}
                  width="500"
                  height="750"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    if (e.target.src !== selectedBook.unsplashBackup && selectedBook.unsplashBackup) {
                      e.target.src = selectedBook.unsplashBackup;
                    }
                  }}
                />
              </div>

              {/* Details */}
              <div className="flex-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-stamp font-bold">
                  {selectedBook.genre} {selectedBook.year ? `· ${selectedBook.year}` : ''}
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-ink mt-1 leading-snug">
                  {selectedBook.title}
                </h3>
                <p className="font-sans text-sm text-ink-80 mt-0.5 font-medium">
                  by <span className="text-ink font-semibold">{selectedBook.author}</span>
                </p>

                {selectedBook.tag && (
                  <div className="mt-3 inline-block px-2.5 py-1 rounded bg-amber-500/10 border border-amber-600/30 text-amber-900 font-mono text-[11px] font-semibold">
                    ★ {selectedBook.tag}
                  </div>
                )}

                <p className="text-ink-80 text-xs sm:text-sm mt-3 leading-relaxed italic-accent border-l-2 border-stamp/50 pl-3">
                  &ldquo;{selectedBook.blurb}&rdquo;
                </p>

                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBook(null);
                      navigate(`/new?search=${encodeURIComponent(selectedBook.title)}`);
                    }}
                    className="label bg-stamp text-paper hover:bg-stamp-ink px-4 py-2.5 rounded text-center no-underline text-xs flex-1 font-semibold flex items-center justify-center gap-1.5 cursor-pointer"
                  >
                    <span>Browse Edition</span>
                    <span className="font-mono">&rarr;</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBook(null);
                      navigate(`/secondhand?search=${encodeURIComponent(selectedBook.author)}`);
                    }}
                    className="label bg-ink text-paper hover:bg-stamp px-3 py-2.5 rounded text-center no-underline text-xs cursor-pointer"
                  >
                    All by Author
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
